import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BugReportIcon from '@mui/icons-material/BugReport';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useAuth } from '../../contexts/AuthContext';
import { getOrCreateClientId, getClientPlatform } from '../../utils/clientId';
import { fetchWithAuth } from '../../api/backendApi';
import { API_ROUTES } from '../../shared/apiRoutes/apiRoutes';
import notificationStore from '../../stores/notificationStore';

// ---------------------------------------------------------------------------
// Helper: format a log line with timestamp
// ---------------------------------------------------------------------------
function logLine(msg) {
  const ts = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `[${ts}] ${msg}`;
}

function formatNotification(n) {
  const parts = [
    `id: ${n.id || n.eventId || '—'}`,
    `severity: ${n.severity || '—'}`,
    `source: ${n.source || '—'}`,
    `title: ${n.title || '—'}`,
    `message: ${n.message || '—'}`,
  ];
  if (n.alertMeta) parts.push(`alertMeta: ${JSON.stringify(n.alertMeta)}`);
  if (n.timestamp) parts.push(`timestamp: ${n.timestamp}`);
  return parts.join(' | ');
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function NotificationDebugConsole({ open, onClose }) {
  const { currentUser, tenantId } = useAuth();
  const userId = currentUser?.uid || '';
  const clientId = getOrCreateClientId();
  const platform = getClientPlatform();

  const [severity, setSeverity] = useState('warning');
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);
  const [copied, setCopied] = useState(false);
  const logEndRef = useRef(null);
  const prevCountRef = useRef(0);

  const addLog = (msg) => setLogs(prev => [...prev, logLine(msg)]);

  // Subscribe to notificationStore — log each NEW notification received
  useEffect(() => {
    if (!open) return;

    const currentNotifs = notificationStore.getAll();
    prevCountRef.current = currentNotifs.length;

    addLog(`NDC opened. tenant=${tenantId} user=${userId} client=${clientId} platform=${platform}`);
    addLog(`notificationStore has ${currentNotifs.length} existing notification(s).`);

    const handler = (allNotifs) => {
      const newCount = allNotifs.length - prevCountRef.current;
      prevCountRef.current = allNotifs.length;
      if (newCount > 0) {
        // notificationStore prepends new items (unshift), so newest are at the front
        const newOnes = allNotifs.slice(0, newCount);
        for (const n of newOnes) {
          addLog(`RECEIVED: ${formatNotification(n)}`);
        }
      }
    };

    const unsub = notificationStore.subscribe(handler);
    return unsub;
  }, [open, tenantId, userId, clientId, platform]);

  // Auto-scroll log area
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Clear logs when dialog closes
  useEffect(() => {
    if (!open) {
      setLogs([]);
      prevCountRef.current = 0;
    }
  }, [open]);

  const handleSend = async () => {
    setSending(true);
    const ts = new Date().toISOString();
    const payload = {
      tenantId,
      severity,
      title: `Debug Test Alert (${severity})`,
      message: `Test from NDC | user=${userId} | client=${clientId} | ${ts}`,
      clientId,
      platform,
    };
    addLog(`SENDING: severity=${severity} to tenant=${tenantId}`);
    try {
      const resp = await fetchWithAuth(API_ROUTES.TEST_EMIT_DEBUG_EVENT, 'POST', payload);
      const eventId = resp?.eventId || resp?.data?.eventId || '—';
      addLog(`SEND OK: eventId=${eventId}`);
    } catch (err) {
      addLog(`SEND FAILED: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleCopy = () => {
    const text = logs.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleClear = () => {
    setLogs([]);
    addLog('Logs cleared.');
  };

  const logText = logs.join('\n');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '70vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReportIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notification Debug Console</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>

        {/* Send Test Alert */}
        <Paper variant="outlined" sx={{ p: 1.5, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Severity</InputLabel>
              <Select value={severity} label="Severity" onChange={e => setSeverity(e.target.value)}>
                <MenuItem value="info">info</MenuItem>
                <MenuItem value="warning">warning</MenuItem>
                <MenuItem value="error">error</MenuItem>
                <MenuItem value="critical">critical</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              color="warning"
              startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              disabled={sending || !tenantId}
              onClick={handleSend}
            >
              Send Test Alert (Tenant-Wide)
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Writes incomingEvent directly to Firestore
            </Typography>
          </Box>
        </Paper>

        {/* Log Area */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: '#1e1e1e',
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'grey.800' }}>
            <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 600 }}>
              Received Notifications Log ({logs.length} entries)
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={handleCopy}
                sx={{ color: copied ? '#4caf50' : '#aaa', textTransform: 'none', fontSize: 11, minWidth: 0, px: 1 }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                size="small"
                startIcon={<DeleteSweepIcon sx={{ fontSize: 14 }} />}
                onClick={handleClear}
                sx={{ color: '#aaa', textTransform: 'none', fontSize: 11, minWidth: 0, px: 1 }}
              >
                Clear
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 1.5,
              fontFamily: 'monospace',
              fontSize: 12,
              lineHeight: 1.6,
              color: '#d4d4d4',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {logText || 'Waiting for notifications...'}
            <div ref={logEndRef} />
          </Box>
        </Paper>

      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 2, py: 1 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
